import React, { useEffect, useState } from "react";
import {
    Box,
    Card,
    CardContent,
    Typography,
    Grid,
    CircularProgress,
    Alert,
    Container,
    LinearProgress,
    Chip,
    Stack,
    Button,
    Tabs,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper
} from "@mui/material";
import {
    CheckCircle,
    PunchClock,
    TrendingUp,
    School,
    ArrowForward,
    EmojiEvents
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import AdminLayout from "../AdminLayout";
import learningService from "../../services/learningService";

export default function LearningProgressPage() {
    const navigate = useNavigate();
    const { userProfile } = useAuth();
    const [progresses, setProgresses] = useState([]);
    const [paths, setPaths] = useState({});
    const [loading, setLoading] = useState(true);
    const [tabValue, setTabValue] = useState(0);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);

                // Fetch all learning paths
                const allPaths = await learningService.getLearningPaths(false);
                const pathMap = {};
                allPaths.forEach((path) => {
                    pathMap[path.id] = path;
                });
                setPaths(pathMap);

                // Fetch student progress
                if (userProfile?.uid) {
                    const progressData = await learningService.getStudentProgressSummary(
                        userProfile.uid
                    );
                    setProgresses(progressData);
                }
            } catch (error) {
                console.error("Error fetching progress data:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [userProfile]);

    const getProgressStats = (progress) => {
        const path = paths[progress.pathId];
        if (!path) return null;

        const levels = path.levels || [];
        const levelStatuses = progress.levelStatus || {};
        const clearedLevels = Object.values(levelStatuses).filter(
            (s) => s.cleared
        ).length;
        const totalLevels = levels.length;
        const progressPercentage = totalLevels > 0 ? (clearedLevels / totalLevels) * 100 : 0;

        return {
            pathName: path.name,
            pathDifficulty: path.difficulty,
            totalCredits: path.creditsOnCompletion || 0,
            earnedCredits: progress.totalCreditsEarned || 0,
            clearedLevels,
            totalLevels,
            progressPercentage,
            isCompleted: progress.pathCompleted || false,
            completedDate: progress.completedAt
        };
    };

    const completedProgresses = progresses.filter(
        (p) => getProgressStats(p)?.isCompleted
    );
    const inProgressProgresses = progresses.filter(
        (p) => !getProgressStats(p)?.isCompleted && getProgressStats(p)?.progressPercentage > 0
    );
    const notStartedProgresses = progresses.filter(
        (p) => getProgressStats(p)?.progressPercentage === 0
    );

    const getFilteredProgresses = () => {
        switch (tabValue) {
            case 0:
                return progresses;
            case 1:
                return completedProgresses;
            case 2:
                return inProgressProgresses;
            case 3:
                return notStartedProgresses;
            default:
                return progresses;
        }
    };

    const getDifficultyColor = (difficulty) => {
        const colors = {
            Beginner: "success",
            Intermediate: "warning",
            Advanced: "error"
        };
        return colors[difficulty] || "default";
    };

    const getDifficultyIcon = (difficulty) => {
        const icons = {
            Beginner: "🟢",
            Intermediate: "🟡",
            Advanced: "🔴"
        };
        return icons[difficulty] || "⚪";
    };

    const getStatusColor = (stats) => {
        if (stats.isCompleted) return "success";
        if (stats.progressPercentage > 0) return "warning";
        return "default";
    };

    const getStatusLabel = (stats) => {
        if (stats.isCompleted) return "Completed";
        if (stats.progressPercentage > 0) return "In Progress";
        return "Not Started";
    };

    if (loading) {
        return (
            <AdminLayout title="Learning Progress">
                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "400px" }}>
                    <CircularProgress />
                </Box>
            </AdminLayout>
        );
    }

    const filteredProgresses = getFilteredProgresses();
    const totalCreditsEarned = progresses.reduce((sum, p) => sum + (p.totalCreditsEarned || 0), 0);

    return (
        <AdminLayout title="Learning Progress">
            <Container maxWidth="lg" sx={{ py: 4 }}>
                {/* Header */}
                <Box sx={{ mb: 4 }}>
                    <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                        📊 Your Learning Progress
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Track your learning paths, earn credits, and achieve your goals
                    </Typography>
                </Box>

                {/* Overall Stats Cards */}
                <Grid container spacing={3} sx={{ mb: 4 }}>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card
                            sx={{
                                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                                color: "white",
                                borderRadius: 2
                            }}
                        >
                            <CardContent sx={{ textAlign: "center" }}>
                                <School sx={{ fontSize: 32, mb: 1 }} />
                                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                                    {progresses.length}
                                </Typography>
                                <Typography variant="caption" sx={{ opacity: 0.9 }}>
                                    Paths Enrolled
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <Card
                            sx={{
                                background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                                color: "white",
                                borderRadius: 2
                            }}
                        >
                            <CardContent sx={{ textAlign: "center" }}>
                                <EmojiEvents sx={{ fontSize: 32, mb: 1 }} />
                                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                                    {completedProgresses.length}
                                </Typography>
                                <Typography variant="caption" sx={{ opacity: 0.9 }}>
                                    Completed
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <Card
                            sx={{
                                background: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
                                color: "white",
                                borderRadius: 2
                            }}
                        >
                            <CardContent sx={{ textAlign: "center" }}>
                                <TrendingUp sx={{ fontSize: 32, mb: 1 }} />
                                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                                    {inProgressProgresses.length}
                                </Typography>
                                <Typography variant="caption" sx={{ opacity: 0.9 }}>
                                    In Progress
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <Card
                            sx={{
                                background: "linear-gradient(135deg, #fa709a 0%, #fee140 100%)",
                                color: "white",
                                borderRadius: 2
                            }}
                        >
                            <CardContent sx={{ textAlign: "center" }}>
                                <School sx={{ fontSize: 32, mb: 1 }} />
                                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                                    {totalCreditsEarned}
                                </Typography>
                                <Typography variant="caption" sx={{ opacity: 0.9 }}>
                                    Credits Earned
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                {/* Tabs */}
                <Card sx={{ mb: 3 }}>
                    <Tabs
                        value={tabValue}
                        onChange={(e, newValue) => setTabValue(newValue)}
                        sx={{ borderBottom: 1, borderColor: "divider" }}
                    >
                        <Tab label={`All (${progresses.length})`} />
                        <Tab label={`Completed (${completedProgresses.length})`} />
                        <Tab label={`In Progress (${inProgressProgresses.length})`} />
                        <Tab label={`Not Started (${notStartedProgresses.length})`} />
                    </Tabs>
                </Card>

                {/* Learning Progress Cards */}
                {filteredProgresses.length === 0 ? (
                    <Alert severity="info" sx={{ textAlign: "center", py: 4 }}>
                        <Typography>
                            {tabValue === 1 && "No completed paths yet. Start a learning path to begin!"}
                            {tabValue === 2 && "No paths in progress. Continue with your learning paths!"}
                            {tabValue === 3 && "You haven't started any paths yet. Explore available paths!"}
                            {tabValue === 0 && "No learning paths available."}
                        </Typography>
                    </Alert>
                ) : (
                    <Grid container spacing={3}>
                        {filteredProgresses.map((progress) => {
                            const stats = getProgressStats(progress);
                            if (!stats) return null;

                            return (
                                <Grid item xs={12} sm={6} md={4} key={progress.id}>
                                    <Card
                                        sx={{
                                            height: "100%",
                                            display: "flex",
                                            flexDirection: "column",
                                            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                                            "&:hover": {
                                                transform: "translateY(-8px)",
                                                boxShadow: 6
                                            },
                                            overflow: "hidden",
                                            position: "relative"
                                        }}
                                    >
                                        {/* Completed Badge */}
                                        {stats.isCompleted && (
                                            <Box
                                                sx={{
                                                    position: "absolute",
                                                    top: 0,
                                                    right: 0,
                                                    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                                                    color: "white",
                                                    p: 1,
                                                    borderRadius: "0 0 0 12px",
                                                    zIndex: 10
                                                }}
                                            >
                                                <CheckCircle sx={{ width: 24, height: 24 }} />
                                            </Box>
                                        )}

                                        {/* Header Section */}
                                        <Box
                                            sx={{
                                                background: "linear-gradient(135deg, #f5f7fa 0%, #eff2f5 100%)",
                                                p: 2.5
                                            }}
                                        >
                                            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, mb: 1 }}>
                                                <Box sx={{ flex: 1 }}>
                                                    <Typography
                                                        variant="h6"
                                                        sx={{
                                                            fontWeight: 700,
                                                            wordBreak: "break-word"
                                                        }}
                                                    >
                                                        {stats.pathName}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {stats.totalLevels} Levels • {stats.totalCredits} Credits
                                                    </Typography>
                                                </Box>
                                            </Box>

                                            <Box sx={{ display: "flex", gap: 1, mt: 1, flexWrap: "wrap" }}>
                                                <Chip
                                                    label={`${getDifficultyIcon(stats.pathDifficulty)} ${stats.pathDifficulty}`}
                                                    size="small"
                                                    color={getDifficultyColor(stats.pathDifficulty)}
                                                    variant="outlined"
                                                />
                                                <Chip
                                                    label={getStatusLabel(stats)}
                                                    size="small"
                                                    color={getStatusColor(stats)}
                                                    variant="filled"
                                                />
                                            </Box>
                                        </Box>

                                        {/* Content Section */}
                                        <CardContent sx={{ flexGrow: 1, pb: 1 }}>
                                            {/* Progress Bar */}
                                            <Box sx={{ mb: 2 }}>
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        justifyContent: "space-between",
                                                        alignItems: "center",
                                                        mb: 1
                                                    }}
                                                >
                                                    <Typography variant="caption" sx={{ fontWeight: 700 }}>
                                                        Progress
                                                    </Typography>
                                                    <Typography
                                                        variant="caption"
                                                        sx={{ fontWeight: 700, color: "primary.main" }}
                                                    >
                                                        {Math.round(stats.progressPercentage)}%
                                                    </Typography>
                                                </Box>
                                                <LinearProgress
                                                    variant="determinate"
                                                    value={stats.progressPercentage}
                                                    sx={{
                                                        height: 8,
                                                        borderRadius: 4,
                                                        backgroundColor: "action.disabledBackground"
                                                    }}
                                                />
                                            </Box>

                                            {/* Levels Progress */}
                                            <Box
                                                sx={{
                                                    p: 1.5,
                                                    background: "linear-gradient(135deg, #f5f7fa 0%, #eff2f5 100%)",
                                                    borderRadius: 1,
                                                    mb: 2
                                                }}
                                            >
                                                <Stack
                                                    direction="row"
                                                    justifyContent="space-around"
                                                    spacing={1}
                                                >
                                                    <Box sx={{ textAlign: "center", flex: 1 }}>
                                                        <Typography variant="caption" color="text.secondary">
                                                            Cleared
                                                        </Typography>
                                                        <Typography
                                                            variant="h6"
                                                            sx={{
                                                                fontWeight: 700,
                                                                color: "success.main"
                                                            }}
                                                        >
                                                            {stats.clearedLevels}/{stats.totalLevels}
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ textAlign: "center", flex: 1 }}>
                                                        <Typography variant="caption" color="text.secondary">
                                                            Earned
                                                        </Typography>
                                                        <Typography
                                                            variant="h6"
                                                            sx={{
                                                                fontWeight: 700,
                                                                color: "info.main"
                                                            }}
                                                        >
                                                            {stats.earnedCredits}/{stats.totalCredits}
                                                        </Typography>
                                                    </Box>
                                                </Stack>
                                            </Box>

                                            {/* Completion Date */}
                                            {stats.isCompleted && stats.completedDate && (
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                                    <CheckCircle sx={{ fontSize: 16, color: "success.main" }} />
                                                    <Typography variant="caption" color="success.main">
                                                        Completed on{" "}
                                                        {new Date(stats.completedDate).toLocaleDateString()}
                                                    </Typography>
                                                </Box>
                                            )}

                                            {/* In Progress Info */}
                                            {!stats.isCompleted && stats.progressPercentage > 0 && (
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                                    <PunchClock sx={{ fontSize: 16, color: "warning.main" }} />
                                                    <Typography variant="caption" color="warning.main">
                                                        Keep going! {stats.totalLevels - stats.clearedLevels} level
                                                        {stats.totalLevels - stats.clearedLevels !== 1 ? "s" : ""} remaining
                                                    </Typography>
                                                </Box>
                                            )}
                                        </CardContent>

                                        {/* Action Button */}
                                        <Box sx={{ p: 2, pt: 1 }}>
                                            <Button
                                                fullWidth
                                                variant="contained"
                                                endIcon={<ArrowForward />}
                                                onClick={() =>
                                                    navigate(`/student/learning/paths/${progress.pathId}`)
                                                }
                                                sx={{
                                                    textTransform: "capitalize",
                                                    fontWeight: 600
                                                }}
                                            >
                                                {stats.isCompleted ? "Review" : stats.progressPercentage > 0 ? "Continue" : "Start"}
                                            </Button>
                                        </Box>
                                    </Card>
                                </Grid>
                            );
                        })}
                    </Grid>
                )}

                {/* Summary Table for Desktop View */}
                {filteredProgresses.length > 0 && (
                    <Card sx={{ mt: 4, display: { xs: "none", md: "block" } }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, p: 2 }}>
                            📋 Detailed Progress Summary
                        </Typography>
                        <TableContainer component={Paper}>
                            <Table>
                                <TableHead sx={{ backgroundColor: "primary.lighter" }}>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 700 }}>Path Name</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>Difficulty</TableCell>
                                        <TableCell align="center" sx={{ fontWeight: 700 }}>
                                            Levels
                                        </TableCell>
                                        <TableCell align="center" sx={{ fontWeight: 700 }}>
                                            Progress
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                                            Credits
                                        </TableCell>
                                        <TableCell align="center" sx={{ fontWeight: 700 }}>
                                            Status
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredProgresses.map((progress) => {
                                        const stats = getProgressStats(progress);
                                        if (!stats) return null;

                                        return (
                                            <TableRow
                                                key={progress.id}
                                                hover
                                                sx={{
                                                    "&:hover": {
                                                        backgroundColor: "action.hover"
                                                    }
                                                }}
                                            >
                                                <TableCell sx={{ fontWeight: 600 }}>
                                                    {stats.pathName}
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={`${getDifficultyIcon(stats.pathDifficulty)} ${stats.pathDifficulty}`}
                                                        size="small"
                                                        color={getDifficultyColor(stats.pathDifficulty)}
                                                        variant="outlined"
                                                    />
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                        {stats.clearedLevels}/{stats.totalLevels}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                        <LinearProgress
                                                            variant="determinate"
                                                            value={stats.progressPercentage}
                                                            sx={{ flex: 1, height: 6 }}
                                                        />
                                                        <Typography variant="caption" sx={{ fontWeight: 700, minWidth: 35 }}>
                                                            {Math.round(stats.progressPercentage)}%
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Chip
                                                        label={`${stats.earnedCredits}/${stats.totalCredits}`}
                                                        color="warning"
                                                        size="small"
                                                    />
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Chip
                                                        label={getStatusLabel(stats)}
                                                        color={getStatusColor(stats)}
                                                        size="small"
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Card>
                )}
            </Container>
        </AdminLayout>
    );
}
